// RemoteScenarioLoader — fetches TicketWave example scenarios from GitHub.
//
// Loads manifest.json (flow-structured per MMNT-4480), lazily fetches
// per-flow topologies and individual scenario files on demand.
// All fetched data is cached in-memory for the session.

import type {
  FlowManifest,
  FlowEntry,
  ManifestEntry,
  SimulationScenario,
  SimulationTopology,
} from '../types/index.ts';
import {
  validateTopology,
  validateSeedStructure,
  formatValidationErrors,
} from '../contracts/moment-ir-validation.ts';
// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const DEFAULT_BASE_URL =
  'https://raw.githubusercontent.com/mmmnt/mmmnt/main/examples/ticketwave/facet';

// Example content supply chain is configurable via Vite env; the public
// TicketWave example remains the default. In headless Node harnesses
// (scripts/facet-e2e.mjs) `import.meta.env` does not exist, so the SAME
// variable is honored from `process.env` — feature-detected, never present
// in a browser build, so prod behavior is unchanged.
export const BASE_URL: string =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_EXAMPLE_BASE_URL) ||
  (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env
    ?.VITE_EXAMPLE_BASE_URL ||
  DEFAULT_BASE_URL;

// Defeat both browser cache and raw.githubusercontent.com CDN edge cache.
// `cache: 'no-cache'` forces browser revalidation; the timestamp query param
// varies the URL so CDN edge nodes treat it as a new resource.
function freshUrl(url: string): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}t=${Date.now()}`;
}
const revalidate: RequestInit = { cache: 'no-cache' };

// ---------------------------------------------------------------------------
// In-memory cache
// ---------------------------------------------------------------------------

let cachedManifest: FlowManifest | null = null;
const scenarioCache = new Map<string, SimulationScenario>();
const topologyCache = new Map<string, SimulationTopology>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch the manifest.json from GitHub. Returns cached copy if available.
 */
export async function fetchManifest(): Promise<FlowManifest> {
  if (cachedManifest) return cachedManifest;

  const response = await fetch(freshUrl(`${BASE_URL}/manifest.json`), revalidate);
  if (!response.ok) {
    throw new Error(`Failed to fetch manifest: ${response.status}`);
  }

  const manifest: FlowManifest = await response.json();
  cachedManifest = manifest;
  return manifest;
}

/**
 * Fetch a topology file for a flow. Returns cached copy if available.
 */
export async function fetchTopology(flow: FlowEntry): Promise<SimulationTopology> {
  const cached = topologyCache.get(flow.flowId);
  if (cached) return cached;

  const response = await fetch(freshUrl(`${BASE_URL}/${flow.topology}`), revalidate);
  if (!response.ok) {
    throw new Error(`Failed to fetch topology "${flow.topology}": ${response.status}`);
  }

  const topology: SimulationTopology = normalizeTopology(await response.json(), flow.topology);

  // Validate before caching — a malformed remote artifact must fail loudly,
  // not render a broken canvas.
  const validation = validateTopology(topology);
  if (!validation.valid) {
    throw new Error(
      `Topology "${flow.topology}" failed validation: ${formatValidationErrors(validation)}`,
    );
  }

  topologyCache.set(flow.flowId, topology);
  return topology;
}

/** Guard remotely-fetched JSON into a SimulationTopology shape or throw. */
function normalizeTopology(data: unknown, fileName: string): SimulationTopology {
  if (!data || typeof data !== 'object') {
    throw new Error(`Topology "${fileName}" is not a JSON object`);
  }
  const obj = data as Record<string, unknown>;
  if (!Array.isArray(obj.lanes) || !Array.isArray(obj.frames)) {
    throw new Error(`Topology "${fileName}" must have lanes and frames arrays`);
  }
  return {
    ...obj,
    lanes: obj.lanes,
    frames: obj.frames,
    connections: Array.isArray(obj.connections) ? obj.connections : [],
    eventRouting:
      obj.eventRouting && typeof obj.eventRouting === 'object' && !Array.isArray(obj.eventRouting)
        ? obj.eventRouting
        : {},
    branchPredicates:
      obj.branchPredicates &&
      typeof obj.branchPredicates === 'object' &&
      !Array.isArray(obj.branchPredicates)
        ? obj.branchPredicates
        : {},
  } as SimulationTopology;
}

/**
 * Fetch a single scenario file by its manifest entry. Returns cached copy if available.
 */
export async function fetchScenario(entry: ManifestEntry): Promise<SimulationScenario> {
  const cached = scenarioCache.get(entry.scenarioId);
  if (cached) return cached;

  const response = await fetch(freshUrl(`${BASE_URL}/${entry.file}`), revalidate);
  if (!response.ok) {
    throw new Error(`Failed to fetch scenario "${entry.file}": ${response.status}`);
  }

  const raw: unknown = await response.json();
  if (
    !raw ||
    typeof raw !== 'object' ||
    typeof (raw as Record<string, unknown>).scenarioId !== 'string' ||
    !Array.isArray((raw as Record<string, unknown>).events) ||
    !Array.isArray((raw as Record<string, unknown>).expectedPath)
  ) {
    throw new Error(
      `Scenario "${entry.file}" is malformed — expected scenarioId, expectedPath, and events`,
    );
  }
  const scenario = raw as SimulationScenario;

  // Envelope-level validation (topology-independent). Full seed-vs-topology
  // validation happens where both are available (ScenarioImportService).
  const validation = validateSeedStructure(scenario);
  if (!validation.valid) {
    throw new Error(
      `Scenario "${entry.file}" failed validation: ${formatValidationErrors(validation)}`,
    );
  }

  scenarioCache.set(entry.scenarioId, scenario);
  return scenario;
}

/**
 * Find the first happy path entry across all flows.
 * Returns [flow, entry] or undefined.
 */
export function findHappyPath(manifest: FlowManifest): [FlowEntry, ManifestEntry] | undefined {
  for (const flow of manifest.flows) {
    const happy = flow.scenarios.find((s) => s.isHappyPath);
    if (happy) return [flow, happy];
  }
  return undefined;
}

/**
 * Clear all cached data (useful for testing or forced refresh).
 */
export function clearCache(): void {
  cachedManifest = null;
  scenarioCache.clear();
  topologyCache.clear();
}

/**
 * Check if a scenario is already cached.
 */
export function isScenarioCached(scenarioId: string): boolean {
  return scenarioCache.has(scenarioId);
}

/**
 * Get a cached scenario without fetching.
 */
export function getCachedScenario(scenarioId: string): SimulationScenario | null {
  return scenarioCache.get(scenarioId) ?? null;
}

/**
 * Check if a topology is already cached for a flow.
 */
export function isTopologyCached(flowId: string): boolean {
  return topologyCache.has(flowId);
}

/**
 * Get a cached topology without fetching.
 */
export function getCachedTopology(flowId: string): SimulationTopology | null {
  return topologyCache.get(flowId) ?? null;
}
